/**
 * Firebase initialization — must be imported before any Firebase SDK usage.
 * Used by src/stores/auth.js (getAuth) and src/lib/http.js (ID token).
 *
 * Fill in VITE_FIREBASE_* env vars in app-materio/.env to connect to your project.
 * Without them the app still loads but Firebase auth won't work.
 */
import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  browserLocalPersistence,
  setPersistence,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

if (!firebaseConfig.apiKey) {
  console.warn(
    "[Firebase] VITE_FIREBASE_* env vars not set — " +
      "login will fail until you add them to app-materio/.env",
  );
}

// Guard against double-init (HMR reloads)
const firebaseApp = getApps().length
  ? getApps()[0]
  : initializeApp(firebaseConfig);

/**
 * authReady — a module-level Promise that resolves the FIRST TIME Firebase
 * fires onAuthStateChanged (i.e. once the persisted session is known).
 *
 * We also explicitly set browserLocalPersistence so tokens land in localStorage
 * (captured by Playwright's storageState) rather than IndexedDB.
 */
export const authReady = (async () => {
  if (!firebaseConfig.apiKey) return;
  // Force localStorage persistence so Playwright can save/restore auth state.
  await setPersistence(getAuth(), browserLocalPersistence);
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(getAuth(), () => {
      unsubscribe();
      resolve();
    });
  });
})();

export default firebaseApp;
